import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Gift, Target, Flame, Lightbulb, ArrowRight } from "lucide-react";

export const BonusSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const spectreFeatures = [
    {
      icon: Target,
      title: "Score Hormozi",
      description: "Désir / probabilité / délai / effort",
    },
    {
      icon: Flame,
      title: "Toast + Roast",
      description: "Ciblage flou, trop vu, promesse molle, mécanisme absent",
    },
    {
      icon: Lightbulb,
      title: "Rebuild",
      description: "Nouvelle promesse + mécanisme unique + objections + plan test 7 jours",
    },
  ];

  return (
    <section 
      ref={ref}
      className="section-padding relative overflow-hidden"
      data-testid="bonus-section"
    >
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-white to-purple-50" />
      
      {/* Decorative elements */}
      <div className="absolute top-10 right-10 w-40 h-40 border-2 border-amber-200/50 rounded-full" />
      <div className="absolute bottom-10 left-10 w-32 h-32 border-2 border-purple-200/50 rounded-full" />

      <div className="lumen-container relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Bonus badge */}
          <motion.div
            className="flex justify-center mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-2 rounded-full font-manrope font-semibold text-sm tracking-wider uppercase">
              <Gift className="w-4 h-4" />
              Bonus exclusif
            </div>
          </motion.div>

          {/* Main card */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-amber-400/20 via-purple-400/20 to-amber-400/20 rounded-3xl blur-xl" />
            
            <div className="relative glass-card p-8 md:p-12 border-2 border-amber-200/50">
              {/* Header */}
              <div className="text-center mb-10">
                <h3 className="font-cinzel text-3xl md:text-4xl tracking-wider text-text-main mb-4">
                  SPECTRE
                </h3>
                <p className="font-cormorant text-xl md:text-2xl text-text-muted italic">
                  Audit d'offre (Formule d'Hormozi + roast utile)
                </p>
              </div>

              {/* Description */}
              <p className="font-manrope text-center text-text-main mb-10 max-w-2xl mx-auto">
                Tu soumets ton offre : <span className="font-semibold text-amber-600">SPECTRE</span> te donne
              </p>

              {/* Features grid */}
              <div className="grid md:grid-cols-3 gap-6 mb-10">
                {spectreFeatures.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    className="text-center p-6 bg-white/50 rounded-2xl"
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  >
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-100 to-purple-100 flex items-center justify-center">
                      <feature.icon className="w-6 h-6 text-amber-600" />
                    </div>
                    <h4 className="font-manrope font-semibold text-text-main mb-2">
                      {feature.title}
                    </h4>
                    <p className="font-manrope text-sm text-text-muted">
                      {feature.description}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Translation box */}
              <motion.div
                className="bg-gradient-to-r from-emerald-50 to-amber-50 rounded-2xl p-6 md:p-8"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <p className="font-manrope font-medium text-text-main mb-2">
                  Traduction :
                </p>
                <p className="font-cormorant text-xl md:text-2xl text-emerald-700">
                  Tu arrêtes de vendre "un truc".
                  <br />
                  <span className="italic">
                    Tu commences à vendre une trajectoire irrésistible, éthique, claire.
                  </span>
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
